import React, { useEffect, useState } from "react";
import api, { extractError, API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader, LoadingState, EmptyState } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Landmark, Pencil, Plus, Loader2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const empty = { denominazione: "", tipologia_giuridica: "", codice_fiscale: "", partita_iva: "", comune: "", provincia: "", telefono: "", email: "", pec: "", referente: "", attivo: true };

const Entities = () => {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [f, setF] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoEntity, setLogoEntity] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoSaving, setLogoSaving] = useState(false);
  const [bust, setBust] = useState(Date.now());

  const load = () => { setLoading(true); api.get("/entities").then(({ data }) => setItems(data)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);
  const openEdit = (e) => { setEdit(e); setF(e ? { ...empty, ...e } : empty); setOpen(true); };
  const openLogo = (e) => { setLogoEntity(e); setLogoFile(null); setLogoOpen(true); };
  const uploadLogo = async () => {
    if (!logoFile) { toast.error("Seleziona un file JPG o PNG"); return; }
    if (!["image/jpeg", "image/jpg", "image/png"].includes((logoFile.type || "").toLowerCase())) { toast.error("Formato non valido: usa JPG o PNG"); return; }
    if (logoFile.size > 3 * 1024 * 1024) { toast.error("Logo troppo grande (max 3MB)"); return; }
    setLogoSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", logoFile);
      await api.post(`/entities/${logoEntity.id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo caricato");
      setLogoOpen(false);
      setBust(Date.now());
      load();
    } catch (e) { toast.error(extractError(e)); } finally { setLogoSaving(false); }
  };
  const save = async () => {
    if (!f.denominazione.trim()) { toast.error("Inserisci la denominazione"); return; }
    setSaving(true);
    try { if (edit) await api.put(`/entities/${edit.id}`, f); else await api.post("/entities", f); toast.success("Ente salvato"); setOpen(false); load(); }
    catch (e) { toast.error(extractError(e)); } finally { setSaving(false); }
  };
  return (
    <div>
      <PageHeader icon={Landmark} title="Enti e strutture" description="Enti e realtà della Rete Azioni Solidali"
        actions={can("entities.write") && <Button onClick={() => openEdit(null)} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white"><Plus className="h-4 w-4 mr-2" /> Nuovo ente</Button>} />
      {loading ? <LoadingState /> : items.length === 0 ? <EmptyState title="Nessun ente" /> : (
        <Card className="divide-y">
          {items.map((e) => (
            <div key={e.id} className="p-4 flex items-center justify-between gap-3" data-testid="entity-row">
              <div className="flex items-center gap-3 min-w-0">
                {e.has_logo ? (
                  <img src={`${API_BASE}/public/entities/${e.id}/logo?v=${bust}`} alt={`Logo ${e.denominazione}`} className="h-11 w-11 rounded-md object-contain border bg-white shrink-0" data-testid={`entity-logo-${e.id}`} />
                ) : (
                  <div className="h-11 w-11 rounded-md border bg-slate-50 flex items-center justify-center shrink-0" aria-hidden="true"><Landmark className="h-5 w-5 text-slate-400" /></div>
                )}
                <div className="min-w-0"><div className="font-medium truncate">{e.denominazione}</div><div className="text-xs text-muted-foreground truncate">{e.tipologia_giuridica} {e.comune ? `· ${e.comune} (${e.provincia})` : ""}</div></div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-[10px] rounded-full px-2 py-0.5 ${e.attivo ? "bg-[color:var(--brand-green-100)] text-[color:var(--brand-green-700)]" : "bg-slate-100 text-slate-500"}`}>{e.attivo ? "Attivo" : "Inattivo"}</span>
                {can("entities.write") && <Button variant="ghost" size="sm" onClick={() => openLogo(e)} title="Carica logo" aria-label={`Carica logo ${e.denominazione}`} data-testid={`entity-logo-btn-${e.id}`}><ImageIcon className="h-4 w-4" /></Button>}
                {can("entities.write") && <Button variant="ghost" size="sm" onClick={() => openEdit(e)} aria-label={`Modifica ${e.denominazione}`}><Pencil className="h-4 w-4" /></Button>}
              </div>
            </div>
          ))}
        </Card>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[92vh] overflow-auto ras-scroll">
          <DialogHeader><DialogTitle className="font-display">{edit ? "Modifica ente" : "Nuovo ente"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2"><Label>Denominazione *</Label><Input value={f.denominazione} onChange={(e) => setF({ ...f, denominazione: e.target.value })} /></div>
            <div><Label>Tipologia giuridica</Label><Input value={f.tipologia_giuridica} onChange={(e) => setF({ ...f, tipologia_giuridica: e.target.value })} /></div>
            <div><Label>Referente</Label><Input value={f.referente} onChange={(e) => setF({ ...f, referente: e.target.value })} /></div>
            <div><Label>Codice fiscale</Label><Input value={f.codice_fiscale} onChange={(e) => setF({ ...f, codice_fiscale: e.target.value })} /></div>
            <div><Label>Partita IVA</Label><Input value={f.partita_iva} onChange={(e) => setF({ ...f, partita_iva: e.target.value })} /></div>
            <div><Label>Comune</Label><Input value={f.comune} onChange={(e) => setF({ ...f, comune: e.target.value })} /></div>
            <div><Label>Provincia</Label><Input value={f.provincia} onChange={(e) => setF({ ...f, provincia: e.target.value })} maxLength={2} /></div>
            <div><Label>Telefono</Label><Input value={f.telefono} onChange={(e) => setF({ ...f, telefono: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
            <div className="flex items-center gap-2"><Switch checked={f.attivo} onCheckedChange={(v) => setF({ ...f, attivo: v })} /><Label>Attivo</Label></div>
          </div>
          <DialogFooter className="gap-2"><Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button><Button onClick={save} disabled={saving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white">{saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={logoOpen} onOpenChange={setLogoOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-display">Logo · {logoEntity?.denominazione}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {logoEntity?.has_logo && !logoFile && (
              <div className="flex items-center justify-center rounded-md border bg-white p-3">
                <img src={`${API_BASE}/public/entities/${logoEntity.id}/logo?v=${bust}`} alt="Logo attuale" className="max-h-28 object-contain" />
              </div>
            )}
            {logoFile && (
              <div className="flex items-center justify-center rounded-md border bg-white p-3">
                <img src={URL.createObjectURL(logoFile)} alt="Anteprima" className="max-h-28 object-contain" />
              </div>
            )}
            <div>
              <Label htmlFor="logo-file">File logo (JPG o PNG, max 3 MB)</Label>
              <Input id="logo-file" type="file" accept="image/png,image/jpeg" onChange={(ev) => setLogoFile(ev.target.files[0])} data-testid="entity-logo-file" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setLogoOpen(false)}>Annulla</Button>
            <Button onClick={uploadLogo} disabled={logoSaving} className="bg-[color:var(--brand-orange-600)] hover:bg-[color:var(--brand-orange-500)] text-white" data-testid="entity-logo-save">{logoSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Carica</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Entities;
